import React, { ChangeEvent, DragEvent, useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Button } from './Button';

interface FileUploadProps {
  label: string;
  accept?: string;
  multiple?: boolean;
  files: File[];
  onChange: (files: File[]) => void;
  helperText?: string;
  error?: string;
  /** Blocks picking, dropping and removing files, e.g. while the chosen file is being processed. */
  disabled?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/** Keeps dropped files honest about `accept` — the browser only enforces it in the picker. */
function matchesAccept(file: File, accept: string | undefined): boolean {
  if (!accept) return true;

  return accept
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean)
    .some((entry) =>
      entry.startsWith('.')
        ? file.name.toLowerCase().endsWith(entry)
        : new RegExp(`^${entry.replace('*', '.*')}$`).test(file.type.toLowerCase()),
    );
}

export const FileUpload: React.FC<FileUploadProps> = ({
  label,
  accept,
  multiple = false,
  files,
  onChange,
  helperText,
  error,
  disabled = false,
}) => {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const apply = (selected: File[]) => {
    if (disabled) return;
    const allowed = selected.filter((file) => matchesAccept(file, accept));
    if (allowed.length === 0) return;
    onChange(multiple ? [...files, ...allowed] : allowed.slice(0, 1));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    apply(Array.from(e.target.files || []));
    // Reset so picking the same file twice in a row still fires onChange.
    e.target.value = '';
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    apply(Array.from(e.dataTransfer.files));
  };

  const handleRemoveFile = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <label className="block text-[12px] font-medium text-slate-600">{label}</label>

      <div
        className={`rounded-xl p-6 text-center transition-colors border border-dashed ${
          disabled
            ? 'cursor-not-allowed opacity-50'
            : 'cursor-pointer hover:border-[#00A871]/60 hover:bg-emerald-50/40'
        } ${error ? 'border-red-400' : dragging ? 'border-[#00A871]' : 'border-slate-300'} ${
          dragging ? 'bg-emerald-50/60' : files.length > 0 ? 'bg-slate-50' : 'bg-white'
        }`}
        aria-disabled={disabled}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" strokeWidth={1.75} />
        <p className="text-[13px] text-slate-700">{t('common:fileUpload.prompt')}</p>
        {helperText && <p className="text-[11px] text-slate-500 mt-1">{helperText}</p>}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={handleFileChange}
        className="hidden"
        aria-label={label}
      />

      {error && <p className="text-[11px] text-red-600">{error}</p>}

      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between gap-3 px-3 py-2 bg-white border border-slate-200 rounded-md"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-slate-900 truncate">{file.name}</p>
                <p className="text-[11px] text-slate-500 tabular-nums">{formatFileSize(file.size)}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveFile(index)}
                disabled={disabled}
                className="!p-1"
                aria-label={t('common:fileUpload.remove')}
              >
                <X className="w-3.5 h-3.5" strokeWidth={2} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
